import { useState, useEffect } from 'react'
import { useSmartNavigation } from '../hooks/useSmartNavigation'
import {
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentChartBarIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  BellAlertIcon,
  ShieldCheckIcon,
  MapPinIcon,
  WifiIcon,
  CogIcon
} from '@heroicons/react/24/outline'
import { checkinsApi, locationsApi, postOrdersApi } from '../services/api'
import { connectionSimulator } from '../services/connectionSimulator'
import { offlineStorage } from '../services/offlineStorage'
import { useDevMode } from '../contexts/DevModeContext'
import toast from 'react-hot-toast'

// Helper function to convert UTC time to user's local time
function convertUTCToLocalTime(utcTimeString: string): Date {
  // Parse the UTC time string and convert to local time
  const utcDate = new Date(utcTimeString)

  // Log the conversion for debugging
  console.log('🕐 UTC to Local conversion:', {
    original: utcTimeString,
    utcISO: utcDate.toISOString(),
    localTime: utcDate.toLocaleString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  return utcDate
}

// Helper function to format time for display
function formatTimeForDisplay(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

// Helper function to format date for display
function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })
}

interface Location {
  id: string
  name: string
  address: string
  description?: string
  coordinates?: {
    lat: number
    lng: number
  }
  centerLat?: number
  centerLng?: number
  radiusMeters?: number
  geoType?: string
}

interface CheckInStatus {
  id: string
  location: string
  locationId: string
  checkInTime: string
  notes?: string
  isWithinGeofence: boolean
}

interface StatusProps {
  user: any
}

export default function Status({ user }: StatusProps) {
  const { navigateToHourlyReport, navigateToIncidentReport, checkAndNavigateToPostOrders } = useSmartNavigation()

  // Get dev mode state from profile
  const { isDevModeEnabled } = useDevMode()

  // State
  const [locations, setLocations] = useState<Location[]>([])
  const [currentCheckIn, setCurrentCheckIn] = useState<CheckInStatus | null>(null)
  const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number } | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [checkInDuration, setCheckInDuration] = useState('')
  const [geofenceStatus, setGeofenceStatus] = useState<'inside' | 'outside' | null>(null)
  const [nearbyLocations, setNearbyLocations] = useState<Location[]>([])
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false)
  const [checkInLoading, setCheckInLoading] = useState(false)
  const [selectedLocationForCheckIn, setSelectedLocationForCheckIn] = useState<Location | null>(null)
  const [checkInNotes, setCheckInNotes] = useState('')

  // Dev mode state
  const [devLatitude, setDevLatitude] = useState('')
  const [devLongitude, setDevLongitude] = useState('')
  const [showDevOptions, setShowDevOptions] = useState(isDevModeEnabled) // Show by default if enabled in profile
  const [devSelectedLocationId, setDevSelectedLocationId] = useState('')

  // Offline sync status
  const [offlineStatus, setOfflineStatus] = useState({
    isOnline: true,
    pendingSync: 0,
    isSimulatingOffline: false
  })

  // Update showDevOptions when profile dev mode changes
  useEffect(() => {
    setShowDevOptions(isDevModeEnabled)
  }, [isDevModeEnabled])

  // Ensure offline simulation is disabled when Dev Mode is off or Dev Options are hidden
  useEffect(() => {
    if (!isDevModeEnabled || !showDevOptions) {
      if (connectionSimulator.isSimulatingOfflineMode()) {
        connectionSimulator.simulateOnline()
        const status = checkinsApi.getOfflineStatus()
        setOfflineStatus(status)
        toast.success('Offline mode disabled')
      }
    }
  }, [isDevModeEnabled, showDevOptions])

  // Load data on mount
  useEffect(() => {
    loadLocations()
    loadCurrentCheckIn()
    getCurrentLocation()
  }, [])

  // Listen for admin checkout events
  useEffect(() => {
    const handleAdminCheckout = (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🚨 Admin checkout event received:', customEvent.detail)

      // Clear current check-in state since user was checked out by admin
      setCurrentCheckIn(null)
      offlineStorage.clearCurrentCheckInSnapshot()

      // Show additional notification
      toast.error('Your shift has been ended by an administrator', {
        duration: 10000,
        icon: '🚨',
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          border: 'none'
        }
      })
    }

    // Add event listener for admin checkout
    window.addEventListener('admin:checkout:forced', handleAdminCheckout)

    // Cleanup
    return () => {
      window.removeEventListener('admin:checkout:forced', handleAdminCheckout)
    }
  }, [])

  // Monitor offline status
  useEffect(() => {
    const updateOfflineStatus = () => {
      const status = checkinsApi.getOfflineStatus()
      // Also refresh pending count from storage to be safe
      setOfflineStatus({ ...status, pendingSync: offlineStorage.getPendingSyncCount() })
    }

    // Update immediately
    updateOfflineStatus()

    // Set up interval to check status
    const interval = setInterval(updateOfflineStatus, 4000)

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('📱 Connection restored')
      updateOfflineStatus()
    }

    const handleOffline = () => {
      console.log('📱 Connection lost')
      updateOfflineStatus()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      clearInterval(interval)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Update duration timer
  useEffect(() => {
    if (currentCheckIn) {
      console.log('🔍 Current check-in:', currentCheckIn)
      const updateDuration = () => {
        const now = new Date()

        // Convert UTC check-in time to local time
        const checkInTime = convertUTCToLocalTime(currentCheckIn.checkInTime)

        // Calculate duration
        const diff = now.getTime() - checkInTime.getTime()
        const hours = Math.floor(diff / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

        setCheckInDuration(`${hours}h ${minutes}m`)

        // Log the duration calculation
        console.log('⏱️ Duration calculation:', {
          now: now.toLocaleString(),
          checkInTime: checkInTime.toLocaleString(),
          diffMs: diff,
          hours,
          minutes,
          duration: `${hours}h ${minutes}m`
        })
      }

      updateDuration()
      const interval = setInterval(updateDuration, 60000) // Update every minute

      return () => clearInterval(interval)
    }
  }, [currentCheckIn])

  const loadLocations = async () => {
    try {
      console.log('🔍 Loading locations...')
      const response = await locationsApi.getLocations()
      console.log('📊 Locations response:', response)

      // Check if response has data property or is direct array
      const locationsData = response.data || response || []
      console.log('📍 Locations data:', locationsData)

      setLocations(locationsData)
      console.log('✅ Locations loaded:', locationsData.length, 'locations')
    } catch (error) {
      console.error('❌ Failed to load locations:', error)
      toast.error('Failed to load locations')
    }
  }

  const loadCurrentCheckIn = async () => {
    try {
      const response = await checkinsApi.getCurrent()
      if (response) {
        setCurrentCheckIn(response)
      }
    } catch (error) {
      console.error('Failed to load current check-in:', error)
    }
  }

  const getCurrentLocation = () => {
    setLocationLoading(true)
    setLocationError('')
    setLocationPermissionDenied(false)

    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by this browser')
      setLocationLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        setLocationLoading(false)
      },
      (error) => {
        let errorMessage = 'Unable to get your location'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions in your browser settings.'
            setLocationPermissionDenied(true)
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
        }
        setLocationError(errorMessage)
        setLocationLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    )
  }

  // Get the effective location for geofence calculations (considering dev mode)
  const getEffectiveLocation = () => {
    if (showDevOptions && devLatitude && devLongitude) {
      return {
        lat: parseFloat(devLatitude),
        lng: parseFloat(devLongitude)
      }
    }
    return currentLocation
  }

  // Calculate geofence status when location and current position are available
  useEffect(() => {
    const effectiveLocation = getEffectiveLocation()
    // If offline simulation is enabled, force outside state and clear nearby
    if (offlineStatus.isSimulatingOffline) {
      setGeofenceStatus('outside')
      setNearbyLocations([])
      return
    }
    if (effectiveLocation && locations.length > 0) {
      // If user is currently checked in, compute distance to that location's geofence
      if (currentCheckIn) {
        const checkedInLocation = locations.find(loc => loc.id === currentCheckIn.locationId)
        if (checkedInLocation && checkedInLocation.centerLat && checkedInLocation.centerLng && checkedInLocation.radiusMeters) {
          const distanceToCheckedIn = calculateDistance(
            checkedInLocation.centerLat,
            checkedInLocation.centerLng,
            effectiveLocation.lat,
            effectiveLocation.lng
          )
          setGeofenceStatus(distanceToCheckedIn <= checkedInLocation.radiusMeters ? 'inside' : 'outside')
        } else {
          // If the checked-in location lacks geofence data, default to outside
          setGeofenceStatus('outside')
        }
        // Nearby locations list is not used while checked in
        setNearbyLocations([])
        return
      }
      // Find nearby locations
      const nearby = locations.filter(location => {
        if (location.centerLat && location.centerLng && location.radiusMeters) {
          const distance = calculateDistance(
            location.centerLat,
            location.centerLng,
            effectiveLocation.lat,
            effectiveLocation.lng
          )
          return distance <= location.radiusMeters
        }
        return false
      })
      setNearbyLocations(nearby)

      // Set geofence status based on nearby locations
      // In dev mode, if we have coordinates, we should be considered "inside" if it correspond to the selected location
      if (showDevOptions && devLatitude && devLongitude && devSelectedLocationId) {
        const selectedLocation = locations.find(loc => loc.id === devSelectedLocationId)
        if (selectedLocation && selectedLocation.centerLat && selectedLocation.centerLng) {
          const distance = calculateDistance(
            selectedLocation.centerLat,
            selectedLocation.centerLng,
            effectiveLocation.lat,
            effectiveLocation.lng
          )
          if (distance <= (selectedLocation.radiusMeters || 0)) {
            setGeofenceStatus('inside')
          } else {
            setGeofenceStatus('outside')
          }
        } else {
          setGeofenceStatus('outside')
        }
      } else if (nearby.length > 0) {
        setGeofenceStatus('inside')
      } else {
        setGeofenceStatus('outside')
      }
    } else {
      setGeofenceStatus(null)
      setNearbyLocations([])
    }
  }, [currentLocation, currentCheckIn, locations, showDevOptions, devLatitude, devLongitude, devSelectedLocationId, offlineStatus.isSimulatingOffline])

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180
    const φ2 = lat2 * Math.PI / 180
    const Δφ = (lat2 - lat1) * Math.PI / 180
    const Δλ = (lon2 - lon1) * Math.PI / 180

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distance in meters
  }

  const handleCheckIn = async (location: Location) => {
    setCheckInLoading(true)
    try {
      // Block check-in when offline simulation is enabled
      if (offlineStatus.isSimulatingOffline) {
        toast.error('Check-in is disabled while Offline Mode is enabled in Developer Options')
        return
      }
      const effectiveLocation = getEffectiveLocation()
      if (!effectiveLocation) {
        throw new Error('Location not available')
      }

      const response = await checkinsApi.checkIn({
        locationId: location.id,
        latitude: effectiveLocation.lat,
        longitude: effectiveLocation.lng,
        notes: checkInNotes
      })

      // Check for pending post orders after successful check-in using existing logic
      const postOrdersResult = await checkAndNavigateToPostOrders(location.id, false)

      if (postOrdersResult.hasPending) {
        toast.success(`Check-in successful! You have ${postOrdersResult.count} pending post order${postOrdersResult.count > 1 ? 's' : ''} to review.`)
      } else {
        toast.success('Check-in successful! No pending post orders.')
      }

      setCurrentCheckIn({
        id: 'temp-id',
        location: location.name,
        locationId: location.id,
        checkInTime: new Date().toISOString(),
        notes: checkInNotes,
        isWithinGeofence: true
      })
      // Save a local snapshot so UI remains accurate if we go offline
      const snapshotId = (response as any)?.id || 'local-temp'
      offlineStorage.setCurrentCheckInSnapshot({
        id: snapshotId,
        location: location.name,
        locationId: location.id,
        checkInTime: new Date().toISOString(),
        notes: checkInNotes
      })
      setCheckInNotes('')
      setSelectedLocationForCheckIn(null)
      toast.success('Check-in completed successfully!')
    } catch (error) {
      console.error('Check-in failed:', error)
      toast.error('Failed to check in. Please try again.')
    } finally {
      setCheckInLoading(false)
    }
  }

  const handleCheckOut = async () => {
    setCheckInLoading(true)
    try {
      if (!currentCheckIn) {
        throw new Error('No active check-in found')
      }

      await checkinsApi.checkOut(currentCheckIn.locationId)
      setCurrentCheckIn(null)
      // Clear local snapshot on checkout
      offlineStorage.clearCurrentCheckInSnapshot()
      toast.success('Check-out completed successfully!')
    } catch (error) {
      console.error('Check-out failed:', error)
      toast.error('Failed to check out. Please try again.')
    } finally {
      setCheckInLoading(false)
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm animate-pulse-orange">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-display-sm font-display text-gradient">Status</h1>
              <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
                Geofence Check-in/Check-out
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Offline Status Indicator (when offline) */}
            {!offlineStatus.isOnline && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <WifiIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                  Offline Mode
                </span>
                {offlineStatus.pendingSync > 0 && (
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-orange-600 dark:text-orange-400">•</span>
                    <span className="text-xs text-orange-600 dark:text-orange-400">
                      {offlineStatus.pendingSync} pending
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Online but with pending offline actions */}
            {offlineStatus.isOnline && offlineStatus.pendingSync > 0 && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <BellAlertIcon className="w-4 h-4 text-yellow-700 dark:text-yellow-300" />
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {offlineStatus.pendingSync} action{offlineStatus.pendingSync > 1 ? 's' : ''} ready to sync
                </span>
                <button
                  onClick={async () => {
                    try {
                      const result = await offlineStorage.syncOfflineActions()
                      toast.success(`Synced ${result.success} actions successfully`)
                      const status = checkinsApi.getOfflineStatus()
                      setOfflineStatus(status)
                    } catch (error) {
                      toast.error('Failed to sync offline actions')
                    }
                  }}
                  className="ml-2 px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors"
                >
                  Sync Now
                </button>
              </div>
            )}
            {checkInDuration && (
              <div className="text-right">
                <div className="text-body-sm text-gray-600 dark:text-tech-400">Checked in for</div>
                <div className="text-heading-lg text-success font-display">{checkInDuration}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dev Options Panel */}
      {isDevModeEnabled && showDevOptions && (
        <div className="premium-card p-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-heading-md text-orange-800 dark:text-orange-200 font-display">Developer Options</h3>
          </div>

          <div className="space-y-4">
            {/* Offline Mode Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-orange-800 dark:text-orange-200">Simulate Offline Mode</div>
                <div className="text-xs text-orange-700 dark:text-orange-300">Blocks network requests and queues actions for sync.</div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!offlineStatus.isOnline && offlineStatus.isSimulatingOffline}
                  onChange={(e) => {
                    if (e.target.checked) {
                      connectionSimulator.simulateOffline()
                      toast.success('Offline mode enabled (simulated)')
                    } else {
                      connectionSimulator.simulateOnline()
                      toast.success('Offline mode disabled')
                    }
                    const status = checkinsApi.getOfflineStatus()
                    setOfflineStatus(status)
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:bg-orange-500 relative">
                  <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            </div>

            {/* Location Selection */}
            <div>
              <label className="block text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
                Quick Location Select
              </label>
              <select
                value={devSelectedLocationId}
                onChange={(e) => {
                  const locationId = e.target.value
                  setDevSelectedLocationId(locationId)
                  if (locationId) {
                    const location = locations.find(loc => loc.id === locationId)
                    if (location && location.centerLat && location.centerLng) {
                      setDevLatitude(location.centerLat.toString())
                      setDevLongitude(location.centerLng.toString())
                    }
                  }
                }}
                className="w-full px-3 py-2 bg-white/80 dark:bg-tech-800/50 border border-orange-300 dark:border-orange-600 rounded-lg text-gray-900 dark:text-tech-100 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
              >
                <option value="">Choose a location to simulate...</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} - {location.address}
                  </option>
                ))}
              </select>
            </div>

            {/* Control Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
              {currentLocation && (
                <button
                  onClick={() => {
                    setDevLatitude(currentLocation.lat.toString())
                    setDevLongitude(currentLocation.lng.toString())
                    setDevSelectedLocationId('')
                  }}
                  className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors"
                >
                  Use Current Location
                </button>
              )}
            </div>

            {/* Status Display */}
            {devLatitude && devLongitude && (
              <div className="mt-3 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <div className="text-sm text-orange-800 dark:text-orange-200">
                  <strong>Simulating:</strong> ({devLatitude}, {devLongitude})
                  <div className="mt-1 text-xs">
                    <strong>Status:</strong> {geofenceStatus === 'inside' ? 'Inside geofence' : 'Outside geofence'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Check-in Status */}
      {currentCheckIn && (
        <div className="premium-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Currently Checked In</h2>
            <div className="flex items-center space-x-2">
              <div className="p-2 rounded-full bg-success/20">
                <CheckCircleIcon className="w-5 h-5 text-success" />
              </div>
              <span className="text-body-sm font-medium text-success">Checked In</span>
            </div>
          </div>

          {geofenceStatus === 'outside' && (
            <div className="mb-4 flex items-start space-x-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700">
              <div className="p-1.5 rounded-full bg-orange-100 dark:bg-orange-900/40">
                <ExclamationTriangleIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-body-sm font-medium text-orange-800 dark:text-orange-200">You're outside the location geofence</div>
                <div className="text-xs text-orange-700 dark:text-orange-300">Move closer to the location or refresh your location.</div>
                <button
                  onClick={getCurrentLocation}
                  className="mt-2 px-3 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 transition-colors"
                >
                  Refresh Location
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-tech-700 to-tech-600">
                <MapPinIcon className="w-4 h-4 text-tech-300" />
              </div>
              <span className="text-body text-gray-900 dark:text-tech-50">
                {typeof currentCheckIn.location === 'string'
                  ? currentCheckIn.location
                  : (currentCheckIn.location as any)?.name || 'Unknown Location'
                }
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
                <ClockIcon className="w-4 h-4 text-white" />
              </div>
              <span className="text-body-sm text-gray-600 dark:text-tech-400">
                Since {formatTimeForDisplay(convertUTCToLocalTime(currentCheckIn.checkInTime))} on {formatDateForDisplay(convertUTCToLocalTime(currentCheckIn.checkInTime))}
              </span>
            </div>
            {checkInDuration && (
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
                  <ClockIcon className="w-4 h-4 text-white" />
                </div>
                <span className="text-body-sm font-medium text-primary-600 dark:text-primary-400">
                  Duration: {checkInDuration}
                </span>
              </div>
            )}
            {currentCheckIn.notes && (
              <div className="bg-gray-50 dark:bg-tech-800/30 p-3 rounded-lg">
                <p className="text-body-sm text-gray-700 dark:text-tech-300 italic">
                  "{currentCheckIn.notes}"
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons for Checked In User */}
          <div className="mt-6 space-y-4">
            {/* Quick Report Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigateToHourlyReport()}
                className="flex items-center space-x-3 p-4 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:-translate-y-0.5"
              >
                <DocumentChartBarIcon className="w-5 h-5" />
                <span className="text-body-sm font-medium">Hourly Report</span>
              </button>
              <button
                onClick={() => navigateToIncidentReport()}
                className="flex items-center space-x-3 p-4 bg-gradient-to-r from-warning to-accent-amber text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <ExclamationTriangleIcon className="w-5 h-5" />
                <span className="text-body-sm font-medium">Incident Report</span>
              </button>
            </div>

            {/* Check Out Button */}
            <button
              onClick={handleCheckOut}
              disabled={checkInLoading}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-error to-red-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {checkInLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <XCircleIcon className="w-5 h-5" />
                  <span className="text-body-sm font-medium">Check Out</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Nearby Locations for Check-in */}
      {!currentCheckIn && (
        <div className="space-y-6">
          {/* Location Status */}
          <div className="premium-card p-6">
            {/* No Nearby Locations Message */}
            {geofenceStatus === 'outside' && !locationLoading && !locationError && (
              <div className="text-center space-y-4">
                <div className="p-4 rounded-full bg-orange-100 dark:bg-orange-900/20 mx-auto w-fit">
                  <ExclamationTriangleIcon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-heading-md text-gray-900 dark:text-tech-50 font-display mb-2">
                    No Locations Nearby
                  </h3>
                  <p className="text-body text-gray-600 dark:text-tech-400 mb-4">
                    You need to be within a geofence to check in. Please move closer to a location or contact your administrator.
                  </p>
                </div>
              </div>
            )}

            {locationLoading ? (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                  <span className="text-body text-gray-600 dark:text-tech-400">Getting your location...</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please allow location access when prompted
                </p>
              </div>
            ) : locationError ? (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <div className="p-3 rounded-full bg-error/20">
                    <ExclamationTriangleIcon className="w-6 h-6 text-error" />
                  </div>
                </div>
                <div>
                  <h3 className="text-heading-md text-gray-900 dark:text-tech-50 font-display mb-2">
                    Location Access Required
                  </h3>
                  <p className="text-body text-gray-600 dark:text-tech-400 mb-4">
                    {locationError}
                  </p>
                  <button
                    onClick={getCurrentLocation}
                    className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : currentLocation ? (
              <div className="text-center space-y-4">
                <div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={getCurrentLocation}
                      className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                    >
                      Refresh Location
                    </button>
                    <button
                      onClick={() => {
                        setCurrentLocation(null)
                        setGeofenceStatus(null)
                        setNearbyLocations([])
                      }}
                      className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Reset Location
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                    <MapPinIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-heading-md text-gray-900 dark:text-tech-50 font-display mb-2">
                    Enable Location Access
                  </h3>
                  <p className="text-body text-gray-600 dark:text-tech-400 mb-4">
                    We need your location to detect when you're within a geofence for check-in.
                  </p>
                  <button
                    onClick={getCurrentLocation}
                    className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
                  >
                    Enable Location Access
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Available Locations for Check-in */}
          {nearbyLocations.length > 0 && (
            <div className="premium-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Available Locations</h2>
                <MapPinIcon className="h-6 w-6 text-primary-500" />
              </div>

              <div className="space-y-4">
                {nearbyLocations.map((location) => (
                  <div key={location.id} className="border border-gray-200 dark:border-tech-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-tech-800/30 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-full bg-green-100 dark:bg-green-800">
                          <MapPinIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <div className="text-body font-medium text-gray-900 dark:text-tech-50">
                            {location.name}
                          </div>
                          <div className="text-body-sm text-gray-600 dark:text-tech-400">
                            {location.address}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="p-1 rounded-full bg-green-100 dark:bg-green-800">
                          <CheckCircleIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">Within Range</span>
                      </div>
                    </div>

                    {selectedLocationForCheckIn?.id === location.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={checkInNotes}
                          onChange={(e) => setCheckInNotes(e.target.value)}
                          placeholder="Any additional notes for this check-in... (optional)"
                          className="w-full px-3 py-2 bg-white/80 dark:bg-tech-800/50 border border-gray-300 dark:border-tech-700 rounded-lg text-gray-900 dark:text-tech-100 placeholder-gray-500 dark:placeholder-tech-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-y"
                          rows={2}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleCheckIn(location)}
                            disabled={checkInLoading}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                          >
                            {checkInLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <CheckCircleIcon className="w-4 h-4" />
                            )}
                            <span className="text-sm font-medium">Check In</span>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLocationForCheckIn(null)
                              setCheckInNotes('')
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedLocationForCheckIn(location)}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Check In to {location.name}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  )
}